'use client'

import { useState, useEffect, useCallback, Key, useRef } from 'react'
import { ConnectorManager } from '../../ConnectorManager'

export interface GrowwHolding {
  isin: Key | null | undefined
  symbol: string
  quantity: string
  locked: string
  averagePrice: number
  lastPrice: number
  pnl: number
}

// Cache for holdings data
interface HoldingsCache {
  data: GrowwHolding[]
  timestamp: number
}

// Global cache that persists between component renders
const GLOBAL_CACHE: { holdings?: HoldingsCache } = {}

// Cache expiry in milliseconds - 30 seconds
const CACHE_EXPIRY = 30000

/**
 * Custom hook for managing Groww holdings data
 */
export function useGrowwHoldings() {
  const [holdings, setHoldings] = useState<GrowwHolding[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Use ref to avoid unnecessary re-renders
  const fetchInProgress = useRef(false)

  const fetchHoldings = useCallback(async (forceRefresh = false) => {
    // Prevent multiple simultaneous fetches
    if (fetchInProgress.current) {
      console.log('Holdings fetch already in progress')
      return
    }
    
    // Check cache first if not forcing refresh
    const now = Date.now()
    if (!forceRefresh && GLOBAL_CACHE.holdings && now - GLOBAL_CACHE.holdings.timestamp < CACHE_EXPIRY) {
      console.log('Using cached holdings data')
      setHoldings(GLOBAL_CACHE.holdings.data)
      return
    }
    
    setIsLoading(true)
    setError(null)
    fetchInProgress.current = true
    
    try {
      // Get the Groww connector
      const connectorManager = ConnectorManager.getInstance()
      const growwConnector = connectorManager.getConnector('groww')
      
      if (!growwConnector) {
        throw new Error('Groww connector not found or not connected')
      }
      
      // Use the getUserAssets method from the connector
      const assets = await growwConnector.getUserAssets()
      
      if (Array.isArray(assets) && assets.length > 0) {
        // Update state and cache
        setHoldings(assets)
        GLOBAL_CACHE.holdings = {
          data: assets,
          timestamp: Date.now()
        }
      } else {
        console.log('No holdings data returned or empty array')
        setHoldings([])
        // Update cache even for empty results to prevent repeated fetching
        GLOBAL_CACHE.holdings = {
          data: [],
          timestamp: Date.now()
        }
      }
    } catch (error: any) {
      console.error('Error fetching holdings:', error)
      setError(error.message)
    } finally {
      setIsLoading(false)
      fetchInProgress.current = false
    }
  }, [])

  useEffect(() => {
    // Fetch holdings on mount, using cache if available
    fetchHoldings(false)
    
    // Set up periodic refresh - every 60 seconds
    const intervalId = setInterval(() => {
      fetchHoldings(false)
    }, 60000)
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId)
  }, [fetchHoldings])

  return {
    holdings,
    isLoading,
    error,
    fetchHoldings: () => fetchHoldings(true) // Force refresh when called directly
  }
}